package com.binayshaw7777.myportfolio.components.sections

import androidx.compose.runtime.Composable
import com.binayshaw7777.myportfolio.AboutMeFontStyle
import com.binayshaw7777.myportfolio.utils.Constants
import com.binayshaw7777.myportfolio.utils.Res
import com.binayshaw7777.myportfolio.utils.Strings
import com.binayshaw7777.myportfolio.widgets.AppearanceAwareImage
import com.binayshaw7777.myportfolio.widgets.StaggeringSolidButton
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.PointerEvents
import com.varabyte.kobweb.compose.css.pointerEvents
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.shapes.Circle
import com.varabyte.kobweb.silk.theme.shapes.clip
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.css.*

val AboutLabelStyle = CssStyle.base {
    Modifier
        .fontSize(14.px)
        .fontWeight(FontWeight.Bold)
        .letterSpacing(0.6.px)
        .color(Colors.White.copyf(alpha = 0.45f))
        .styleModifier { property("text-transform", "uppercase") }
}

val AboutStatementStyle = CssStyle {
    base {
        Modifier
            .fontSize(60.px)
            .fontWeight(700)
            .lineHeight(1.25)
            .color(Colors.White)
            .letterSpacing((-0.5).px)
            .styleModifier { property("max-width", "18ch") }
    }
    Breakpoint.MD { Modifier.fontSize(38.px) }
}

val AboutBodyCapsStyle = CssStyle.base {
    Modifier
        .fontSize(12.px)
        .fontWeight(700)
        .lineHeight(1.6)
        .letterSpacing(0.9.px)
        .color(Colors.White.copyf(alpha = 0.55f))
        .styleModifier {
            property("text-transform", "uppercase")
            property("max-width", "52ch")
        }
}

val AboutFactsRowStyle = CssStyle.base {
    Modifier
        .margin(top = 18.px)
        .display(DisplayStyle.Flex)
        .flexWrap(FlexWrap.Wrap)
        .gap(10.px)
}

val AboutCapsRichTextStyle = CssStyle.base {
    Modifier
        .fontSize(14.px)
        .fontWeight(FontWeight.Normal)
        .lineHeight(1.6)
        .letterSpacing(0.5.px)
        .styleModifier {
            property("text-transform", "uppercase")
            property("max-width", "60ch")
        }
}

@OptIn(ExperimentalComposeWebApi::class)
@Composable
fun AboutMe() {
    val pageContent = rememberPageContext()
    val breakpoint = rememberBreakpoint()

    val aboutMeImageSize = when (breakpoint) {
        Breakpoint.ZERO -> 200
        Breakpoint.SM -> 225
        Breakpoint.MD -> 250
        Breakpoint.LG -> 300
        else -> 300
    }

    val aboutMeImageContentAlignment = when (breakpoint) {
        Breakpoint.ZERO -> Alignment.Center
        Breakpoint.SM -> Alignment.CenterStart
        else -> Alignment.CenterStart
    }

    Box(
        modifier = Modifier.fillMaxWidth().minHeight(100.vh).margin(top = 100.px, bottom = 100.px).id("about"),
        contentAlignment = Alignment.Center
    ) {

        SimpleGrid(
            modifier = Modifier.fillMaxWidth(),
            numColumns = numColumns(base = 1, sm = 2),
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxHeight(100.vh)
                    .padding(leftRight = 18.px)
                    .justifyContent(JustifyContent.Center)
            ) {
                Box(
                    modifier = Modifier
                        .size(aboutMeImageSize.px) // 🔥 fixed container
                        .styleModifier { position(Position.Relative) },
                    contentAlignment = Alignment.Center
                ) {
                    // Photo
                    Image(
                        src = Res.MY_PICTURE,
                        modifier = Modifier
                            .fillMaxSize(85.percent)
                            .objectFit(ObjectFit.Cover)
                            .clip(Circle())
                            .pointerEvents(PointerEvents.None)
                    )

                    Image(
                        src = Res.CIRCLE_SCRIBBLE_GIF,
                        modifier = Modifier
                            .size(aboutMeImageSize.px + 18.px) // ✅ bigger than photo
                            .objectFit(ObjectFit.Cover)
                            .styleModifier {
                                position(Position.Absolute)
                                left(50.percent)
                                top(50.percent)
                                transform { translate((-50).percent, (-50).percent) }
                                opacity(0.9)
                                pointerEvents(PointerEvents.None)
                            }
                    )
                }
            }

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxHeight(100.vh)
            ) {
                Column {
                    SpanText(Strings.ABOUT_ME, modifier = Modifier.fontSize(14.px).color(Colors.Gray))
                    SpanText(
                        Strings.ABOUT_ME_FIRST_PARA,
                        modifier = AboutMeFontStyle.toModifier()

                    )
                    SpanText(
                        Strings.ABOUT_ME_SECOND_PARA,
                        modifier = AboutMeFontStyle.toModifier()
                    )
                }

                StaggeringSolidButton(
                    text = Strings.MY_RESUME, speed = 0.1, trailingIcon = {
                        AppearanceAwareImage(
                            src = Res.TOP_RIGHT_ARROW,
                            isLight = ColorMode.current == ColorMode.LIGHT
                        )
                    },
                    isLight = ColorMode.current == ColorMode.LIGHT,
                    onClick = {
                        pageContent.router.navigateTo(Constants.RESUME_URL)
                    }
                )
            }
        }
    }
}